import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Stack, Shelf } from "capitalrx-components-2.0/lib/layout";
import { Body, Title } from "capitalrx-components-2.0/lib/typography";
import { Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Form, SubmitButton, useForm, RadioGroup, DataDropdown } from "capitalrx-components-2.0/lib/forms";
import { Field as TextField } from "capitalrx-components-2.0/lib/forms/Textbox/Field";
import { useRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";

import { getProviderManagementAPI, getApiBaseUrl, getPACaseAPI } from "@/api";
import { CaseSearchErrors } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL, isDate, numberValidator, formatDateWithSlashes } from "@/utils";

import styles from "./PASearchForm.module.scss";
import { BackLink } from "../BackLink/BackLink";

export interface CaseSearchForm {
  readonly case_id: number;
  readonly prescriber_npi: string;
  readonly external_member_id: string;
  readonly member_date_of_birth: string;
  readonly drug_gpi?: string;
  readonly search_by?: "case_id" | "drug";
}

interface HidenCaseSearchForm {
  readonly case_id: string | number;
  readonly prescriber_npi: string;
  readonly external_member_id: string;
  readonly member_date_of_birth: string;
  readonly drug_gpi?: string;
  readonly search_by?: "case_id" | "drug";
}

interface PASearchFormProps {
  setSelectedTab?: (string) => void;
  showTitle?: boolean;
}

export const PASearchForm: FC<PASearchFormProps> = ({ setSelectedTab, showTitle=false }) => {
  const navigate = useNavigate();
  const { getAuthToken } = useContext(AuthenticationContext);
  const { primary_color, primary_hover_color } = useContext(ThemeSelectorContext);
  const apiBaseUrl = getApiBaseUrl();

  const form = useForm<HidenCaseSearchForm>();
  const { setValue } = form;

  const [error, setError] = useState<string>();

  const [originalMemberId, setOriginalMemberId] = useState<string>("");
  const [originalDOB, setOriginalDOB] = useState<string>("");
  const [originalCaseId, setOriginalCaseId] = useState<string | number>();
  const [searchBy, setSearchBy] = useState<"case_id" | "drug">("case_id");
  const [searchValue, setSearchValue] = useState("");

  // Drug search data source for GPI-8 search
  interface MDDBDrug {
    ndc?: string | null;
    drug_name?: string | null;
    gpi?: string | null;
    dosage?: string | null;
    dosage_form?: string | null;
  }

  const drugLabelProvider = useCallback((row: MDDBDrug) => {
    const drugName = row.drug_name || "";
    const gpi = row.gpi || "";
    const gpi8 = gpi.substring(0, 8);
    const dosage = row.dosage ? ` (${row.dosage})` : "";
    const form = row.dosage_form ? ` - ${row.dosage_form}` : "";
    return gpi8 ? `(GPI-8: ${gpi8}) - ${drugName}${dosage}${form}` : `${drugName}${dosage}${form}`;
  }, []);

  const drugDataSource = useRemoteTableDataSource<MDDBDrug>(
    `${apiBaseUrl}/mddb_drug/list`,
    (row) => row.gpi?.substring(0, 8) || "",
    {
      pageSize: 15,
      idFilterName: "drug_name_or_ndc",
      enabled: searchBy === "drug" && searchValue.length >= 4,
      authTokenRequired: false,
    },
  );

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam === CaseSearchErrors.NOT_FOUND) {
      setError("No case found - check that all information is correct");
      if (setSelectedTab) setSelectedTab("lookup");
    }

    const externalMemberId = searchParams.get("external_member_id");
    const memberDOB = searchParams.get("member_date_of_birth");
    const caseId = searchParams.get("case_id");

    if (externalMemberId) {
      setOriginalMemberId(externalMemberId);
      const maskedMemberId = externalMemberId.replace(/[0-9a-zA-Z]/g, "*");
      setValue("external_member_id", maskedMemberId);
    }

    if (memberDOB) {
      setOriginalDOB(memberDOB);
      const maskedDOB = memberDOB.replace(/[0-9]/g, "*");
      setValue("member_date_of_birth", maskedDOB);
    }

    if (caseId) {
      setOriginalCaseId(caseId);
      const maskedCaseId = caseId.toString().replace(/[0-9]/g, "*");
      setValue("case_id", maskedCaseId);
    }

    const otherFields: (keyof HidenCaseSearchForm)[] = [
      "prescriber_npi",
    ];

    otherFields.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        setValue(field, value);
      }
    });
    setSearchParams({});
    getProvider();
  }, []);

  const getProvider = useCallback(async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const pmResponse = await getProviderManagementAPI(authToken);
      if (!pmResponse?.provider?.billing_npi) return;

      setValue("prescriber_npi", pmResponse.provider.billing_npi);
    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
    }
  }, [getAuthToken, setValue]);

  const handleSearch = useCallback(async () => {
    logEvent(ANALYTIC_EVENTS.PA_CASE_SEARCH.search);

    const formValues = form.getValues();

    const memberId = originalMemberId || formValues.external_member_id;
    const dob = originalDOB || formValues.member_date_of_birth;
    const caseId = originalCaseId || formValues.case_id;
    const drugGpi = formValues.drug_gpi;

    setOriginalMemberId(memberId);
    setOriginalDOB(dob);
    setOriginalCaseId(caseId);

    if (searchBy === "case_id") {
      if (!caseId) {
        setError("Please provide a Case ID");
        return;
      }
      if (numberValidator(caseId?.toString())) {
        setError("Case ID must be a number");
        return;
      }
    } else {
      if (!drugGpi) {
        setError("Please select a drug");
        return;
      }
    }

    const dateError = isDate(dob);
    if (dob && dateError !== true) {
      setError(dateError as string);
      return;
    }

    // If we have a case_id, navigate directly to that case
    if (searchBy === "case_id" && caseId) {
      const params = {
        case_id: caseId,
        prescriber_npi: formValues.prescriber_npi,
        external_member_id: memberId,
        member_date_of_birth: dob,
        error: CaseSearchErrors.NOT_FOUND,
      };

      const encodedParams = new URLSearchParams();
      for (const key in params) {
        if (params[key]) {
          encodedParams.append(key, params[key]);
        }
      }

      navigate({
        pathname: getThemeURL("PA_CASE"),
        search: encodedParams.toString(),
      });
      return;
    }

    try {
      const gpi8 = drugGpi.substring(0, 8);

      const caseResponse = await getPACaseAPI({
        drug_gpi: gpi8,
        prescriber_npi: formValues.prescriber_npi,
        external_member_id: memberId,
        member_date_of_birth: dob || "",
      });

      // Navigate to case page with case_id and validation params
      const params = new URLSearchParams();
      params.append("case_id", caseResponse.case_id.toString());
      params.append("prescriber_npi", formValues.prescriber_npi);
      params.append("external_member_id", memberId);
      if (dob) params.append("member_date_of_birth", dob);

      navigate({
        pathname: getThemeURL("PA_CASE"),
        search: params.toString(),
      });
    } catch (error) {
      console.error("Failed to search for case:", error);
      setError("No case found - check that all information is correct");
    }
  }, [navigate, originalMemberId, originalDOB, originalCaseId, form, searchBy]);

  const handleMemberIdFocus = useCallback(() => {
    setValue("external_member_id", originalMemberId);
  }, [originalMemberId, setValue]);

  const handleMemberIdBlur = useCallback(() => {
    const memberId = form.getValues("external_member_id");
    setOriginalMemberId(memberId);
    const replaceVal = memberId.replace(new RegExp("[0-9a-zA-Z]", "g"), "*");
    setValue("external_member_id", replaceVal);
  }, [setValue, form]);

  const handleDOBFocus = useCallback(() => {
    setValue("member_date_of_birth", originalDOB);
  }, [originalDOB, setValue]);

  const handleDOBBlur = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = memberDOB.replace(new RegExp("[0-9]", "g"), "*");
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);

  const handleCaseIdFocus = useCallback(() => {
    setValue("case_id", originalCaseId || "");
  }, [originalCaseId, setValue]);

  const handleCaseIdBlur = useCallback(() => {
    const caseId = form.getValues("case_id");
    setOriginalCaseId(caseId);
    const replaceVal = caseId.toString().replace(new RegExp("[0-9]", "g"), "*");
    setValue("case_id", replaceVal);
  }, [setValue, form]);

  const handleChange = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = formatDateWithSlashes(memberDOB);
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const noOptionsMessage = useMemo(() => {
    if (searchValue.length < 4) {
      return "Start typing to see results";
    }

    if (searchValue.length >= 4) {
      return "No options";
    }

    return null;
  }, [searchValue]);

  return (
    <div className={styles.CaseSearchContainer}>
      <Stack spacing="large" testId="test__prior_authorization_search_container">
        {showTitle && (
          <Stack spacing="small">
            <Stack spacing="none">
              <BackLink linkText="Home" />
              <Title testId="test__prior_authorization_search_heading">
                Prior Authorizations
              </Title>
            </Stack>
            <Body testId="test__prior_authorization_search_description">
              Find an existing case to add documents/check the status, or start a new case
            </Body>
          </Stack>
        )}
        <Form form={form} onSubmit={handleSearch}>
          <Stack>
            <TextField required label="NPI" name="prescriber_npi" testId="test__prior_authorization_search_NPI" />
            <Shelf alignment="top" spacing="medium">
              <RadioGroup
                label="Search By"
                value={searchBy}
                onChange={(value) => setSearchBy(value as "case_id" | "drug")}
                orientation="horizontal"
                testId="test__prior_authorization_search_by"
              >
                <RadioGroup.Option value="case_id">Case ID</RadioGroup.Option>
                <RadioGroup.Option value="drug">Drug</RadioGroup.Option>
              </RadioGroup>
              {searchBy === "case_id" && (
                <div onFocus={handleCaseIdFocus} onBlur={handleCaseIdBlur} style={{ flex: 1 }}>
                  <TextField required label="Case ID" name="case_id" className={styles.textFieldWrapper} testId="test__prior_authorization_search_case_ID" />
                </div>
              )}
              {searchBy === "drug" && (
                <div style={{ flex: 1 }}>
                  <DataDropdown.Field
                    dataSource={drugDataSource}
                    optionLabelProvider={drugLabelProvider}
                    label="Drug (GPI-8)"
                    name="drug_gpi"
                    placeholder="Search"
                    instructions="Search by drug name, NDC/GPI, or exact HCPCS code"
                    searchField="drug_name_or_ndc"
                    required
                    onSearchChange={handleSearchChange}
                    noOptionsMessage={noOptionsMessage}
                    testId="test__prior_authorization_search_drug"
                  />
                </div>
              )}
            </Shelf>
            <div onFocus={handleMemberIdFocus} onBlur={handleMemberIdBlur}>
              <TextField required label="Member ID" name="external_member_id" className={styles.textFieldWrapper} testId="test__prior_authorization_search_member_ID" />
            </div>
            <div onFocus={handleDOBFocus} onBlur={handleDOBBlur} onChange={handleChange}>
              <TextField
                instructions="mm/dd/yyyy"
                required
                label="Patient Date of Birth"
                name="member_date_of_birth"
                placeholder="__/__/____"
                className={styles.textFieldWrapper}
                testId="test__prior_authorization_search_DOB"
              />
            </div>
            {error && <Message appearance={Messages.Error} visible>{error}</Message>}
            <Shelf>
              <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)} testId="test__prior_authorization_search_button">
                Search
              </SubmitButton>
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        </Form>
      </Stack>
    </div>
  );
};
